import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell } from "lucide-react";
import { useNotificationContext } from "../context/NotificationContext";

const timeAgo = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// Header (~52px) + up to 360px of scrollable list + padding — the dropdown's own real
// max height, used to keep it fully on-screen regardless of where its trigger button sits
// (e.g. low inside the mobile nav menu, where the button's position alone would place the
// dropdown's bottom edge well past the viewport).
const DROPDOWN_MAX_HEIGHT = 420;

const NotificationBell = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const buttonRef = useRef(null);
  const { unreadCount, notifications, loading, loadList, markRead, markAllRead } = useNotificationContext();

  const toggle = () => {
    const next = !isOpen;
    if (next && buttonRef.current) {
      setAnchorRect(buttonRef.current.getBoundingClientRect());
      loadList();
    }
    setIsOpen(next);
  };

  // Portaled to <body> below (see render) so it can never get clipped by an ancestor's
  // overflow:hidden — the mobile menu wrapper in Header.js needs overflow-hidden for its
  // own slide-open/close height animation, and a plain in-place `absolute` dropdown nested
  // inside that wrapper gets silently cut off once it grows taller than the menu's own
  // animated height. Same overflow-clipping trap as the old MenuItem.css inner-submenu bug.
  // Since a portal moves the DOM node out from under its visual parent, position is computed
  // from the trigger button's real screen position instead of relying on CSS `absolute`.
  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => setIsOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label="Notifications"
        aria-expanded={isOpen}
        className={
          "relative h-10 w-10 grid place-items-center rounded-full border transition " +
          (isOpen ? "border-brand-300 bg-brand-50 text-brand-700" : "border-gray-300 text-gray-600 hover:bg-gray-50")
        }
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {isOpen && anchorRect && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                style={{
                  position: "fixed",
                  top: Math.max(8, Math.min(anchorRect.bottom + 8, window.innerHeight - DROPDOWN_MAX_HEIGHT - 8)),
                  right: Math.max(8, window.innerWidth - anchorRect.right),
                }}
                className="w-[320px] max-w-[90vw] rounded-xl border border-gray-200 bg-white shadow-elevated z-50 overflow-hidden"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <span className="text-sm font-bold text-gray-900">Notifications</span>
                  <button
                    type="button"
                    onClick={markAllRead}
                    className="text-xs font-semibold text-brand-700 hover:text-brand-800"
                  >
                    Mark all read
                  </button>
                </div>
                <div className="max-h-[360px] overflow-y-auto">
                  {loading ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">No notifications yet.</div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => {
                          markRead(n.id);
                          setIsOpen(false);
                          if (n.link && onNavigate) onNavigate(n.link);
                        }}
                        className={
                          "block w-full text-left px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 " +
                          (n.read ? "" : "bg-brand-50/60")
                        }
                      >
                        <div className="text-sm font-semibold text-gray-900">{n.title}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{n.message}</div>
                        <div className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdOn)}</div>
                      </button>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default NotificationBell;
